import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { StyledAppSelect } from '@nxsys/components/AppSelect/index.styled';
import { defaultDocumentTypeOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import type { Moment } from 'moment';
import moment from 'moment';
import React, { useEffect } from 'react';
import { Form } from 'antd';
import type { FormProps } from 'antd/lib';

export type ReportByValue = 'documentType' | 'receivedOn' | 'expiryDate';

export type EmployeeReportByFilters = {
  reportBy: ReportByValue[];
  documentType?: string;
  receivedOn?: Moment | null;
  expiryDate?: Moment | null;
};

type FormValues = {
  reportBy: ReportByValue[];
  documentType?: string;
  receivedOn?: string;
  expiryDate?: string;
};

type EmployeeReportByFilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  value?: EmployeeReportByFilters;
  onApply: (value: EmployeeReportByFilters) => void;
};

const REPORT_BY_OPTIONS: { value: ReportByValue; label: string }[] = [
  { value: 'documentType', label: 'Document Type' },
  { value: 'receivedOn', label: 'Received On' },
  { value: 'expiryDate', label: 'Expiry Date' },
];

const EmployeeReportByFilterModal: React.FC<EmployeeReportByFilterModalProps> = ({
  title,
  isOpen,
  toggle,
  value,
  onApply,
}: EmployeeReportByFilterModalProps) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<FormValues>();
  const reportBy = Form.useWatch('reportBy', form);
  const watchValue = Form.useWatch([], form);

  const selectedReportBy = (reportBy ?? []) as ReportByValue[];

  const ableToClearFilter =
    watchValue &&
    (selectedReportBy.length > 0 ||
      !!watchValue.documentType ||
      !!watchValue.receivedOn ||
      !!watchValue.expiryDate);

  useEffect(() => {
    if (isOpen && value) {
      form.setFieldsValue({
        reportBy: value.reportBy ?? [],
        documentType: value.documentType,
        receivedOn: value.receivedOn?.toISOString(),
        expiryDate: value.expiryDate?.toISOString(),
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const updates: Partial<FormValues> = {};

    if (!selectedReportBy.includes('documentType')) {
      updates.documentType = undefined;
    }
    if (!selectedReportBy.includes('receivedOn')) {
      updates.receivedOn = undefined;
    }
    if (!selectedReportBy.includes('expiryDate')) {
      updates.expiryDate = undefined;
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [selectedReportBy.join(',')]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values: FormValues) => {
    const result: EmployeeReportByFilters = {
      reportBy: values.reportBy ?? [],
      documentType: undefined,
      receivedOn: null,
      expiryDate: null,
    };

    if (selectedReportBy.includes('documentType') && values.documentType) {
      result.documentType = values.documentType;
    }

    if (selectedReportBy.includes('receivedOn') && values.receivedOn) {
      result.receivedOn = moment(values.receivedOn);
    }

    if (selectedReportBy.includes('expiryDate') && values.expiryDate) {
      result.expiryDate = moment(values.expiryDate);
    }

    onApply(result);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item
          name="reportBy"
          label={messages['invoices.reportBy'] ?? 'Report By'}
          initialValue={[]}
        >
          <StyledAppSelect
            mode="multiple"
            options={[...REPORT_BY_OPTIONS]}
            placeholder={messages['form.pleaseSelect']}
            size="large"
            allowClear
          />
        </Form.Item>

        {selectedReportBy.includes('documentType') && (
          <Form.Item
            name="documentType"
            label={messages['common.documentType'] ?? 'Document Type'}
          >
            <StyledAppSelect
              options={defaultDocumentTypeOptions}
              placeholder={messages['form.pleaseSelect']}
              size="large"
              allowClear
            />
          </Form.Item>
        )}

        {selectedReportBy.includes('receivedOn') && (
          <Form.Item
            key="received-on-field"
            name="receivedOn"
            label={messages['common.receivedOn'] ?? 'Received On'}
          >
            <AppDatePicker
              placeholder={messages['common.selectDate']}
              onChangeValue={(date) => {
                form.setFieldsValue({
                  receivedOn: (date as Moment)?.toISOString(),
                });
              }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        )}

        {selectedReportBy.includes('expiryDate') && (
          <Form.Item
            key="expiry-date-field"
            name="expiryDate"
            label={messages['common.expiryDate'] ?? 'Expiry Date'}
          >
            <AppDatePicker
              placeholder={messages['common.selectDate']}
              onChangeValue={(date) => {
                form.setFieldsValue({
                  expiryDate: (date as Moment)?.toISOString(),
                });
              }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default EmployeeReportByFilterModal;
